import {
  MapLocationIconOutline,
  PhoneIcon,
  RatingStars,
  WayBetweenLocationsIcon,
} from "@App/Components/common/Icons";
import { GarageListItemPremiumServices } from "@App/Components/GarageListItem/GarageListItemPremiumServices";
import { GarageNotBookablePill } from "@App/Components/GarageListItem/GarageNotBookablePill";
import { Hr } from "@App/Components/Hr";
import { useCountry } from "@App/hooks/useCountry";
import type { State } from "@App/state";
import type { Garage, Service } from "@App/types";
import { GarageHasBillingGroups, GarageHasPhone } from "@App/utils/conditionals/garages";
import { ServiceShouldDisplayReviews } from "@App/utils/conditionals/services";
import { getConfig } from "@App/utils/getConfig";
import type React from "react";
import { Fragment } from "react";
import type { TFunction } from "react-i18next";
import { useSelector } from "react-redux";

type Props = {
  garage: Garage;
  bookable: boolean;
  service?: Service;
  prefix?: string;
  handleClick: () => void;
  showPhoneNumber: boolean;
  setShowPhoneNumber: (value: boolean) => void;
  displayPremiumServices: boolean;
  showAsNonBookable?: boolean;
  t: TFunction;
};

export const GarageDetails: React.FC<Props> = ({
  garage,
  bookable,
  service,
  prefix,
  handleClick,
  showPhoneNumber,
  setShowPhoneNumber,
  displayPremiumServices,
  showAsNonBookable,
  t,
}) => {
  const organization = useSelector((state: State) => state.orders.organization);
  const countryCode = useCountry();

  // check if reviews should be shown
  const config = getConfig(organization);
  const orgShouldShowReviews = config.reviews.show;

  const showDistance = !garage.nationwide_service && garage.distance > 0;
  const showTravelTime = !garage.nationwide_service && (garage.travel_time ?? 0) > 0;

  return (
    <div className="px-5 xl:px-0 mt-4 second mb-4">
      <div className="flex flex-col-reverse gap-2 xl:flex-row xl:justify-between xl:items-center items-start">
        <button
          type="button"
          className={`font-serif font-bold text-lg mb-1 text-left ${
            bookable && !showAsNonBookable ? "cursor-pointer" : "cursor-default"
          }`}
          onClick={handleClick}
        >
          <span className="mr-2">
            {prefix} {garage.name}
          </span>
          {GarageHasBillingGroups(garage) && (
            <span className="font-sm sm:font-base text-sm font-normal">
              (
              {garage?.billingGroups?.map((bg, idx) => (
                <Fragment key={`${bg.display_name}-${idx}`}>
                  {bg.display_name}
                  {idx < (garage?.billingGroups?.length ?? 0) - 1 ? ", " : ""}
                </Fragment>
              ))}
              )
            </span>
          )}
        </button>

        {!bookable && <GarageNotBookablePill text={t("garageList.notBookable")} />}
      </div>

      <div className="flex justify-between xl:justify-start">
        <div className="flex flex-col xl:flex-row gap-x-3">
          <div className="flex items-center gap-1.5 order-1">
            <MapLocationIconOutline />
            <div className="font-xs sm:font-base">
              {garage.nationwide_service
                ? t("garageList.mobileService", { context: countryCode })
                : `${garage.city} - ${garage.postcode} - ${garage.street}`}
            </div>
          </div>
          {(showDistance || showTravelTime) && (
            <div className="flex items-center gap-1.5 order-0">
              <WayBetweenLocationsIcon />
              <div className="font-xs sm:font-base">
                {showDistance && <>{(garage.distance / 1000).toFixed(2).replace(".", ",")} km</>}
                {showTravelTime && (
                  <>
                    {showDistance && " | "}
                    {garage.travel_time} Minuten
                  </>
                )}
              </div>
            </div>
          )}

          {!bookable && GarageHasPhone(garage) && (
            <div className="flex items-center gap-1.5 order-2 xl:mt-0 mt-1">
              <PhoneIcon />
              <div className="font-xs sm:font-base">
                {showPhoneNumber ? (
                  garage.phone
                ) : (
                  <button
                    type="button"
                    className="underline"
                    onClick={() => setShowPhoneNumber(true)}
                  >
                    {t("garageList.showPhoneNumber")}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {bookable && service && ServiceShouldDisplayReviews(service) && orgShouldShowReviews && (
          <div className="flex items-center">
            <div className="scale-75 translate-x-2">
              <RatingStars type="rating" percent={garage.rating} />
            </div>
            <div className="font-xs sm:font-base">
              <span className="font-bold">
                {((garage.rating / 100) * 5).toFixed(2).replace(".", ",") ?? "0"}
              </span>
              ({garage.review_count || "0"})
            </div>
          </div>
        )}
      </div>
      {(bookable || garage.premium_services.length > 0) && (
        <Hr bookable={bookable} className={!bookable ? "my-4" : undefined} />
      )}
      {!bookable && displayPremiumServices && (
        <GarageListItemPremiumServices
          garage={garage}
          service={service}
          organization={organization}
          bookable={bookable}
        />
      )}
    </div>
  );
};
